import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/hooks/useI18n';
import { SharedLink, CreateSharedLinkData, UpdateSharedLinkData } from './models/shared-link.model';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, RefreshCw } from 'lucide-react';

interface SharedLinkModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSharedLinkData | UpdateSharedLinkData, linkId?: string) => Promise<boolean>;
  editingLink?: SharedLink | null;
}

interface ItineraryOption {
  id: string;
  name: string;
}

// Generate a random password using crypto API
const generateRandomPassword = (length: number = 12): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
};

export const SharedLinkModal: React.FC<SharedLinkModalProps> = ({
  open,
  onClose,
  onSubmit,
  editingLink,
}) => {
  const { t } = useI18n();
  const [itineraryId, setItineraryId] = useState('');
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [itineraries, setItineraries] = useState<ItineraryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingItineraries, setLoadingItineraries] = useState(false);
  const [errors, setErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open && !editingLink) {
      fetchItineraries();
    }
  }, [open]);

  useEffect(() => {
    if (editingLink) {
      setItineraryId(editingLink.itinerary_id);
      setPassword(editingLink.password || '');
      setExpiresAt(editingLink.expires_at ? editingLink.expires_at.split('T')[0] : '');
    } else {
      resetForm();
    }
  }, [editingLink]);

  const fetchItineraries = async () => {
    setLoadingItineraries(true);
    try {
      const { data, error } = await supabase.rpc('get_dashboard_itineraries', {
        p_itinerary_status: null,
      });

      if (error) {
        console.error('[SharedLinkModal] Error fetching itineraries:', error);
        return;
      }

      // Map to simplified interface (only id and title needed)
      const mapped = (data || []).map((item: any) => ({
        id: item.itinerary_id,
        name: item.itinerary_title,
      }));
      setItineraries(mapped);
    } catch (err) {
      console.error('[SharedLinkModal] Unexpected error:', err);
    } finally {
      setLoadingItineraries(false);
    }
  };

  const resetForm = () => {
    setItineraryId('');
    setPassword('');
    setExpiresAt('');
    setErrors(new Set());
  };

  const generatePassword = () => {
    const generated = generateRandomPassword(12);
    setPassword(generated);
  };

  const validateForm = (): boolean => {
    const newErrors = new Set<string>();

    if (!editingLink && !itineraryId) {
      newErrors.add('itinerary_id');
    }

    setErrors(newErrors);
    return newErrors.size === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let data: CreateSharedLinkData | UpdateSharedLinkData;

      if (editingLink) {
        // Edit mode: only update password and expires_at (null clears the field)
        data = {
          password: password || null,
          expires_at: expiresAt || null,
        };
      } else {
        // Create mode: include itinerary_id
        data = {
          itinerary_id: itineraryId,
          password: password || undefined,
          expires_at: expiresAt || undefined,
        };
      }

      const success = await onSubmit(data, editingLink?.id);

      if (success) {
        handleClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={editingLink ? t('shared.editLink') : t('shared.createLink')}
    >
      <div className="space-y-4">
        <div className="space-y-4 p-6">
        {/* Itinerary Select - Only for create mode */}
        {!editingLink && (
          <div>
            <Label htmlFor="itinerary_id">
              {t('shared.itinerary')} <span className="text-red-500">*</span>
            </Label>
            <Select value={itineraryId} onValueChange={setItineraryId} disabled={loadingItineraries}>
              <SelectTrigger
                id="itinerary_id"
                className={errors.has('itinerary_id') ? 'border-red-500' : ''}
              >
                <SelectValue placeholder={loadingItineraries ? t('common.loading') : t('shared.selectItinerary')} />
              </SelectTrigger>
              <SelectContent>
                {itineraries.map((itinerary) => (
                  <SelectItem key={itinerary.id} value={itinerary.id}>
                    {itinerary.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.has('itinerary_id') && (
              <p className="text-sm text-red-500 mt-1">{t('common.required')}</p>
            )}
          </div>
        )}

        {/* Display itinerary name in edit mode */}
        {editingLink && (
          <div>
            <Label>{t('shared.itinerary')}</Label>
            <p className="text-sm text-muted-foreground mt-1">{editingLink.itinerary_name}</p>
            <p className="text-xs text-muted-foreground">{t('common.notEditable')}</p>
          </div>
        )}

        {/* Password */}
        <div>
          <Label htmlFor="password">
            {t('shared.password')} <span className="text-muted-foreground text-xs">({t('common.optional')})</span>
          </Label>
          <div className="flex gap-2">
            <Input
              id="password"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('shared.passwordPlaceholder')}
            />
            <Button
              type="button"
              variant="outline"
              onClick={generatePassword}
              className="shrink-0"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('shared.generate')}
            </Button>
          </div>
        </div>

        {/* Expires At */}
        <div>
          <Label htmlFor="expires_at">
            {t('shared.expiresAt')} <span className="text-muted-foreground text-xs">({t('common.optional')})</span>
          </Label>
          <DatePicker
            value={expiresAt}
            onSelect={setExpiresAt}
            placeholder={t('shared.expiresAt')}
            minDate={new Date()}
          />
        </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between gap-2 p-4 border-t border-gray-200">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editingLink ? t('common.save') : t('common.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
